import '../styles/globals.css';
import type { AppProps, AppContext } from 'next/app';
import '@livekit/components-styles';
import '@livekit/components-styles/prefabs';
import { DefaultSeo } from 'next-seo';
import { ThemeProvider, useTheme } from '../components/shared/ThemeProvider';
import { AuthProvider } from '../lib/auth';
import { Toaster } from 'react-hot-toast';
import { FALLBACK_THEME_CONFIG, getThemeForSSR } from '../lib/theme-utils';
import React from 'react';
import '../instrumentation-client';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import { AuthGuard } from '../components/shared/AuthGuard';
import { defaultConfig, RuntimeConfigProvider, useRuntimeConfig } from '../lib/runtime-config';
import { RoomPreferencesProvider } from '../lib/RoomPreferencesContext';
import { useRouter } from 'next/router';
import { NavigationProvider } from '../lib/navigation';

function AppContent({ Component, pageProps }: AppProps) {
    const { theme } = useTheme();
    // Use theme from ThemeProvider, fallback to initialTheme only if theme is still loading
    const themeConfig = theme;
    const { publicDomain } = useRuntimeConfig();

    // Prevent Chrome Translate from breaking React DOM
    React.useEffect(() => {
        const handleTranslateError = (event: ErrorEvent) => {
            if (event.message?.includes('insertBefore') || event.message?.includes('removeChild') || event.message?.includes('Node')) {
                console.warn('DOM manipulation error (possibly from Chrome Translate) suppressed:', event.message);
                event.preventDefault();
                event.stopPropagation();
                return true;
            }
        };

        window.addEventListener('error', handleTranslateError, true);
        return () => window.removeEventListener('error', handleTranslateError, true);
    }, []);

    return (
        <>
            <DefaultSeo
                title={themeConfig.title}
                titleTemplate="%s"
                defaultTitle={themeConfig.title}
                description={themeConfig.slogan}
                openGraph={{
                    url: publicDomain,
                    images: [
                        {
                            url: `${publicDomain}${themeConfig.openGraph}`,
                            width: 1200,
                            height: 630,
                            type: 'image/png'
                        }
                    ],
                    site_name: themeConfig.title
                }}
                additionalMetaTags={[
                    {
                        name: 'viewport',
                        content: 'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes'
                    },
                    {
                        property: 'theme-color',
                        content: themeConfig.colors.brand
                    }
                ]}
                additionalLinkTags={[
                    {
                        rel: 'icon',
                        href: themeConfig.favicon
                    },
                    {
                        rel: 'apple-touch-icon',
                        href: themeConfig.appleTouch,
                        sizes: '180x180'
                    },
                    {
                        rel: 'mask-icon',
                        href: themeConfig.safariPinnedTab,
                        color: themeConfig.colors.brand
                    }
                ]}
            />
            <AuthGuard>
                <Component {...pageProps} />
            </AuthGuard>
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 3000,
                    style: {
                        background: '#fff',
                        color: '#333',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        border: '1px solid #e5e7eb'
                    }
                }}
            />
        </>
    );
}

function NextNavigationBridge({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const adapter = React.useMemo(
        () => ({
            query: router.query,
            pathname: router.asPath.split('?')[0],
            push: async (url: string) => {
                await router.push(url);
            },
            replace: async (url: string) => {
                await router.replace(url);
            }
        }),
        [router, router.query, router.asPath]
    );

    return <NavigationProvider adapter={adapter}>{children}</NavigationProvider>;
}

function MyApp(props: AppProps) {
    return (
        <ErrorBoundary>
            <RuntimeConfigProvider
                publicDomain={props.pageProps.publicDomain}
                livekitProjectId={props.pageProps.livekitProjectId}
                showFirstNameOnly={props.pageProps.showFirstNameOnly}
            >
                <ThemeProvider initialTheme={props.pageProps.initialTheme} initialThemeName={props.pageProps.initialThemeName}>
                    <AuthProvider>
                        <RoomPreferencesProvider>
                            <NextNavigationBridge>
                                <AppContent {...props} />
                            </NextNavigationBridge>
                        </RoomPreferencesProvider>
                    </AuthProvider>
                </ThemeProvider>
            </RuntimeConfigProvider>
        </ErrorBoundary>
    );
}

const getInitialRuntimeConfig = (key: 'publicDomain' | 'livekitProjectId'): string | undefined => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    const pageProps = (window as typeof window & {
        __NEXT_DATA__?: { props?: { pageProps?: Record<string, unknown> } };
    }).__NEXT_DATA__?.props?.pageProps;

    const value = pageProps?.[key];
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
};

const firstNonEmpty = (...values: Array<string | undefined>): string | undefined => {
    return values.find((value) => typeof value === 'string' && value.trim() !== '')?.trim();
};

MyApp.getInitialProps = async (appContext: AppContext) => {
    const { req } = appContext.ctx;

    // Fetch theme server-side or use fallback
    const initialTheme = req ? await getThemeForSSR(req) : FALLBACK_THEME_CONFIG;

    const publicDomain = firstNonEmpty(
        process.env.NEXT_PUBLIC_DOMAIN,
        getInitialRuntimeConfig('publicDomain'),
        defaultConfig.publicDomain
    )!;
    const livekitProjectId = firstNonEmpty(
        process.env.LK_PROJECT_ID,
        getInitialRuntimeConfig('livekitProjectId'),
        process.env.NEXT_PUBLIC_LK_PROJECT_ID,
        defaultConfig.livekitProjectId
    )!;

    const showFirstNameOnly = process.env.NEXT_PUBLIC_SHOW_FIRST_NAME_ONLY === 'true';

    return {
        pageProps: {
            initialTheme,
            initialThemeName: 'default',
            publicDomain,
            livekitProjectId,
            showFirstNameOnly
        }
    };
};

export default MyApp;
